import { z } from 'zod';
import { PLAN_IDS } from './account';
import { ACTIVITY_OUTCOMES, ACTIVITY_SOURCES } from './admin';
import { BROWSERS, DEVICES, OPERATING_SYSTEMS } from './events';

/** The requests whose text the model reads, as the request log keeps them. */
export const REQUEST_LOG_FEATURES = ['rewrite', 'tool', 'explanation', 'alternatives'] as const;
export type RequestLogFeature = (typeof REQUEST_LOG_FEATURES)[number];

/**
 * How a logged request ended: as a rewrite's activity does, or turned away before the model saw
 * it, such as asking to explain a version no rewrite returned.
 */
export const REQUEST_LOG_OUTCOMES = [...ACTIVITY_OUTCOMES, 'rejected'] as const;
export type RequestLogOutcome = (typeof REQUEST_LOG_OUTCOMES)[number];

/**
 * Signs of an attack. The first eight are found in what was sent, the rest in what the model
 * answered. Writing about these subjects can trip them too: they're for looking into, not
 * verdicts.
 */
export const REQUEST_LOG_FLAGS = [
  /** Asks to ignore, reveal, or repeat the instructions. */
  'prompt-extraction',
  /** Chat-template or role markup, such as <|im_start|> or "system:". */
  'role-markup',
  /** Names from Lyrmo's own instructions, known only to someone who has seen them. */
  'prompt-marker',
  /** Zero-width, bidirectional, or tag characters that hide words. */
  'hidden-characters',
  /** Asks for the database, keys, passwords, or the server's environment. */
  'data-access',
  'sql',
  'script',
  /** Shell commands, or paths to the server's files. */
  'shell',
  /** The model repeated its instructions; the answer was held back. */
  'leaked-instructions',
  /** The model added a link or an address; the answer was held back. */
  'added-link',
  /** The model wrote far more than the text; the answer was held back. */
  'too-long',
  /** Some explanations or other words were dropped by the checks. */
  'dropped',
] as const;
export type RequestLogFlag = (typeof REQUEST_LOG_FLAGS)[number];

export const REQUEST_LOG_VIEWS = ['all', 'flagged'] as const;
export type RequestLogView = (typeof REQUEST_LOG_VIEWS)[number];

/** Days a request is kept before it's deleted. */
export const REQUEST_LOG_DAYS = 30;

/** Requests to a page of the log. */
export const REQUEST_LOGS_PAGE_SIZE = 10;
/** Requests to a page of a visitor's, on their own page. */
export const VISITOR_REQUESTS_PAGE_SIZE = 10;

const CURSOR = /^[A-Za-z0-9_-]{1,100}$/;

const count = z.int().nonnegative();

const page = z.coerce.number().pipe(z.int().positive().max(100_000)).default(1);

const ListFields = {
  view: z.enum(REQUEST_LOG_VIEWS).default('all'),
  feature: z.enum(REQUEST_LOG_FEATURES).optional(),
  /** An account's email or id, an API key's id, a visitor id, or an IP address, or part of one. */
  who: z.string().trim().max(254).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(REQUEST_LOGS_PAGE_SIZE),
};

export const RequestLogQuerySchema = z.object({ ...ListFields, page });

/** What the log is asked for; each part left out takes its default. */
export interface RequestLogQuery {
  readonly view?: RequestLogView;
  readonly feature?: RequestLogFeature;
  readonly who?: string;
  readonly page?: number;
  readonly limit?: number;
}

/**
 * Words to look for in the sealed texts, with the list's choices. Texts are opened one by one to
 * look, so this scans a bounded batch. Sent in the body, so the words stay out of addresses and
 * logs.
 */
export const RequestLogSearchSchema = z.object({
  ...ListFields,
  /** Older than this entry. */
  before: z.string().regex(CURSOR).optional(),
  text: z.string().trim().min(1).max(200),
});
export type RequestLogSearch = z.input<typeof RequestLogSearchSchema>;

/** Named parts of a request or answer, such as its text and tone, each as text. */
const FieldsSchema = z.record(z.string(), z.string());
export type RequestLogFields = z.infer<typeof FieldsSchema>;

const RequestLogWhoSchema = z.object({
  userId: z.string().nullable(),
  /** The account's email when the request was made. */
  email: z.string().nullable(),
  apiKeyId: z.string().nullable(),
  /** The visitor cookie's random id; API keys have none. */
  visitor: z.string().nullable(),
  ip: z.string().nullable(),
  /** Where Cloudflare places the address, as a two-letter code. */
  country: z.string().nullable(),
  userAgent: z.string().nullable(),
  /** What the user agent names; null without one. */
  platform: z
    .object({
      device: z.enum(DEVICES),
      os: z.enum(OPERATING_SYSTEMS),
      browser: z.enum(BROWSERS),
    })
    .nullable(),
});

/** What a request was like, without anything written in it: kept in the clear. */
export const RequestLogShapeSchema = z.object({
  /** Each part sent, by name, and its characters. */
  sent: z.record(z.string(), count),
  /** Each part the writer got back, and its characters; empty when nothing was. */
  returned: z.record(z.string(), count),
  /**
   * Choices from Lyrmo's own lists: tone, length, language, and tool. A tone of the writer's own
   * is "custom", a tool of theirs "own", and an instruction followed once "once".
   */
  options: z.record(z.string(), z.string()),
  /** The kind of error, such as UPSTREAM_TIMEOUT; null when there was none. */
  error: z.string().nullable(),
});
export type RequestLogShape = z.infer<typeof RequestLogShapeSchema>;

export const RequestLogSummarySchema = z.object({
  id: z.string(),
  at: z.iso.datetime(),
  feature: z.enum(REQUEST_LOG_FEATURES),
  outcome: z.enum(REQUEST_LOG_OUTCOMES),
  source: z.enum(ACTIVITY_SOURCES),
  who: RequestLogWhoSchema,
  flags: z.array(z.enum(REQUEST_LOG_FLAGS)),
  /** Characters of everything sent: the text, instructions, and context. */
  characters: count,
  durationMs: count,
  /** Null for requests logged before shapes were kept. */
  shape: RequestLogShapeSchema.nullable(),
});

/** One request, without its texts, and more about who sent it. */
export const RequestLogSchema = RequestLogSummarySchema.extend({
  /** False when the texts were sealed with another key than the server has now. */
  readable: z.boolean(),
  /** The sender's account as it is now; null without one, or once it's deleted. */
  account: z.object({ plan: z.enum(PLAN_IDS), joinedAt: z.iso.datetime() }).nullable(),
  /**
   * The sender's requests over the days the log keeps: the account's, or without one, the IP
   * address's.
   */
  recent: z.object({
    requests: count,
    flagged: count,
    ips: count,
    firstAt: z.iso.datetime().nullable(),
  }),
});

/** A request's sealed texts, opened. */
export const RequestLogTextSchema = z.object({
  input: FieldsSchema,
  /** What the writer got back; null when nothing was. */
  output: FieldsSchema.nullable(),
  /** The model's answer as it came, when the checks held it back or cut parts of it. */
  answer: FieldsSchema.nullable(),
  /** Why it failed, for the logs. */
  error: z.string().nullable(),
});

export const RequestLogSearchResultSchema = z.object({
  entries: z.array(RequestLogSummarySchema),
  /** Where older entries continue; null at the oldest. */
  before: z.string().nullable(),
  /** How many requests were opened to find these. */
  scanned: count,
});

/** A visitor's requests, for their page: one page of them, `limit` to a page. */
export const VisitorRequestLogQuerySchema = z.object({
  page,
  limit: z.coerce.number().int().min(1).max(100).default(VISITOR_REQUESTS_PAGE_SIZE),
});

/** What a visitor's requests are asked for; each part left out takes its default. */
export interface VisitorRequestLogQuery {
  readonly page?: number;
  readonly limit?: number;
}

/**
 * The requests one browser sent, signed in or not, over the days the log keeps: how many of each
 * feature and how they ended, and a page of them, newest first, without their texts.
 */
export const AdminVisitorRequestsSchema = z.object({
  /** Whether requests are being logged: off until the server has a key to seal them with. */
  enabled: z.boolean(),
  retentionDays: z.int().positive(),
  /** Their requests of each feature by how they ended, over every page. */
  features: z.record(z.enum(REQUEST_LOG_FEATURES), z.record(z.enum(REQUEST_LOG_OUTCOMES), count)),
  flagged: count,
  entries: z.array(RequestLogSummarySchema),
  /** Their requests over every page. */
  total: count,
  page: z.int().positive(),
  pageSize: z.int().positive(),
});

export const AdminRequestLogsSchema = z.object({
  /** Whether requests are being logged: off until the server has a key to seal them with. */
  enabled: z.boolean(),
  retentionDays: z.int().positive(),
  totals: z.object({
    requests: count,
    flagged: count,
    heldBack: count,
    /** Accounts, or networks without one, that sent anything flagged. */
    flaggedSources: count,
  }),
  /** Who sent the most flagged requests: an account, or an IP address without one. */
  topSources: z.array(
    z.object({
      userId: z.string().nullable(),
      email: z.string().nullable(),
      ip: z.string().nullable(),
      /** Distinct IP addresses the account sent them from. */
      ips: count,
      flagged: count,
      lastAt: z.iso.datetime(),
    }),
  ),
  entries: z.array(RequestLogSummarySchema),
  /** Requests that match, over every page. */
  total: count,
  page: z.int().positive(),
  pageSize: z.int().positive(),
  /** Administrators who read the logs lately, newest first. */
  reads: z.array(
    z.object({
      admin: z.string(),
      at: z.iso.datetime(),
      /** A list and its filters, a search, or one request's text revealed. */
      detail: z.string(),
    }),
  ),
});

/** The request log's totals alone, as the Overview shows them: no one's requests, so no read. */
export const AdminRequestLogTotalsSchema = AdminRequestLogsSchema.pick({
  enabled: true,
  retentionDays: true,
  totals: true,
});

/** Who sent the most flagged requests, on a page of their own; the read is recorded. */
export const AdminRequestLogSendersSchema = AdminRequestLogsSchema.pick({
  enabled: true,
  retentionDays: true,
  topSources: true,
});

/** Who read the logs lately, on a page of their own; that read is recorded too. */
export const AdminRequestLogReadsSchema = AdminRequestLogsSchema.pick({ reads: true });

export type RequestLogSummary = z.infer<typeof RequestLogSummarySchema>;
export type RequestLog = z.infer<typeof RequestLogSchema>;
export type RequestLogText = z.infer<typeof RequestLogTextSchema>;
export type RequestLogSearchResult = z.infer<typeof RequestLogSearchResultSchema>;
export type AdminRequestLogs = z.infer<typeof AdminRequestLogsSchema>;
export type AdminRequestLogTotals = z.infer<typeof AdminRequestLogTotalsSchema>;
export type AdminRequestLogSenders = z.infer<typeof AdminRequestLogSendersSchema>;
export type AdminRequestLogReads = z.infer<typeof AdminRequestLogReadsSchema>;
export type AdminVisitorRequests = z.infer<typeof AdminVisitorRequestsSchema>;
